import React, { Dispatch, SetStateAction, useCallback, useEffect } from 'react';
import lookie from 'lookie';
import { useLocation } from 'react-router';
import { omit } from 'lodash-es';
import { EditMemberInfoScene, TrackerCustomEvent, useTracker } from 'common-package';
import { OrientationType, ScreenOrientation } from '@capawesome/capacitor-screen-orientation';
import { useDispatch, useSelector } from 'react-redux';
import { ALL_PAGES, SELECTED_CUSTOMER_ID } from '@/constant';
import useNav from '@/hooks/useNav';
import { selectDevicePlatform, selectedStoreInfo } from '@/store/device';
import defaultAvatarImg from '@/assets/image/default-avatar.png';
import defaultFemaleAvatarImg from '@/assets/image/default-women-avatar.png';
import { setCurrentCustomer } from '@/store/customer';
import { maskPhoneNumber, maskPhoneNumberInterNational, showBirthday } from '@/utils';
import cx from './index.module.scss';

const MemberItem: React.FC<
  GetCustomerProfileResponse & {
    isVerticalScreen: boolean;
    isTipOpen: boolean;
    setIsTipOpen: Dispatch<SetStateAction<boolean>>;
  }
> = props => {
  const {
    phoneNumber,
    name,
    age,
    servedParts,
    isServicing,
    avatarUrl,
    birthday,
    customerId,
    gender,
    isVerticalScreen,
    isTipOpen,
    setIsTipOpen,
  } = props;
  const storeInfo = useSelector(selectedStoreInfo);
  const nav = useNav();
  const dispatch = useDispatch();
  const devicePlatform = useSelector(selectDevicePlatform);
  const location = useLocation();
  const { sendEvent } = useTracker();
  const handleSelectMember = useCallback(() => {
    dispatch(setCurrentCustomer(omit(props, ['isTipOpen', 'setIsTipOpen'])));

    if (location.state?.action === 'ai-consultation') {
      lookie.set(SELECTED_CUSTOMER_ID, props.customerId);

      if (devicePlatform === 'android') {
        ScreenOrientation.lock({ type: OrientationType.PORTRAIT_PRIMARY });

        setIsTipOpen(true);
      } else {
        nav(ALL_PAGES.AI_CONSULTATION);
      }
    } else {
      lookie.set(SELECTED_CUSTOMER_ID, props.customerId);
      nav(ALL_PAGES.MEMBER_DETAIL);
    }
  }, [devicePlatform, dispatch, location.state?.action, nav, props, setIsTipOpen]);

  useEffect(() => {
    if (isTipOpen && isVerticalScreen && devicePlatform === 'android') {
      nav(ALL_PAGES.AI_CONSULTATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVerticalScreen, isTipOpen, devicePlatform]);

  return (
    <div className={cx('member-item')} onClick={handleSelectMember}>
      <div className={cx('header')}>
        <div className={cx('left')}>
          <img
            src={avatarUrl ? avatarUrl : gender === 'Female' ? defaultFemaleAvatarImg : defaultAvatarImg}
            alt="avatar"
            className={cx('img')}
            onError={e => {
              const target = e.target as HTMLImageElement;
              target.src = defaultAvatarImg;
            }}
          />
        </div>
        <div className={cx('right')}>
          <div className={cx('box1')}>
            <div className={cx('name')}>
              <div className={cx('name-text')}>{name}</div>
              {isServicing && <div className={cx('is-serving')}>服务中</div>}
            </div>
            <div className={cx('phone')}>
              {storeInfo?.phoneNumberCheck === 1
                ? maskPhoneNumber(phoneNumber)
                : maskPhoneNumberInterNational(phoneNumber)}
            </div>
          </div>
          <div className={cx('box2')}>
            <div className={cx('item', 'age')}>{showBirthday(age, birthday)}</div>
          </div>
        </div>
      </div>

      <div className={cx('info')}>
        <div className={cx('info-item')}>理疗部位：{servedParts?.filter(Boolean)?.join('、')}</div>
        <div
          className={cx('info-edit-text')}
          onClick={event => {
            event.preventDefault();
            event.stopPropagation();
            sendEvent?.(TrackerCustomEvent.EditMemberInfoClk, { et: 'CLK', c1: EditMemberInfoScene.SearchMember });
            lookie.set(SELECTED_CUSTOMER_ID, customerId);
            nav(ALL_PAGES.EDIT_MEMBER, { state: { action: 'edit', customerId: customerId, isSearchMember: true } });
          }}
        >
          编辑
        </div>
      </div>
    </div>
  );
};

export default MemberItem;
